import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { behavioralAPI, jobsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Brain, Send, Copy, Trash2, FileText, Users, Link2, RefreshCw, Target } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { BarGraph, DivergingGraph, TRAIT_COLORS } from '../components/behavioral/DiscGraphs';
import BehavioralFitPanel from '../components/behavioral/BehavioralFitPanel';

const TERRA = '#0076A3';

const statusBadge = (status) => {
  const map = {
    completed: { bg: 'rgba(16,185,129,0.12)', color: '#059669', label: 'Completed' },
    in_progress: { bg: 'rgba(245,158,11,0.14)', color: '#B45309', label: 'In progress' },
    invited: { bg: 'rgba(100,116,139,0.12)', color: '#64748B', label: 'Invited' },
  };
  return map[status] || map.invited;
};

function StyleTag({ code, label }) {
  if (!code) return <span style={{ color: '#A8A29E', fontSize: '0.8rem' }}>—</span>;
  const primary = code[0];
  return (
    <span data-testid="behavioral-style-tag" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', fontWeight: 600, color: '#002A38' }}>
      <span style={{ width: 10, height: 10, borderRadius: 3, background: TRAIT_COLORS[primary] }} />
      {label || code}
    </span>
  );
}

export default function BehavioralAssessmentsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ candidate_name: '', candidate_email: '', job_id: '' });
  const [inviting, setInviting] = useState(false);
  const [createdLink, setCreatedLink] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [compareData, setCompareData] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [fitJobId, setFitJobId] = useState('');
  const fitPanelRef = useRef(null);

  const selectFitJob = (id) => {
    setFitJobId(id);
    if (id) setTimeout(() => fitPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  };

  const load = useCallback(async () => {
    try {
      const res = await behavioralAPI.listSessions();
      setItems(res.data || []);
    } catch (e) {
      toast.error('Failed to load psychometric assessments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { jobsAPI.list().then((r) => setJobs(r.data || [])).catch(() => {}); }, []);

  const copyLink = (link) => {
    navigator.clipboard?.writeText(link).then(() => toast.success('Invite link copied')).catch(() => toast.error('Could not copy'));
  };

  const submitInvite = async () => {
    if (!inviteForm.candidate_name.trim() || !inviteForm.candidate_email.trim()) {
      toast.error('Name and email are required'); return;
    }
    setInviting(true); setCreatedLink('');
    try {
      const { data } = await behavioralAPI.invite({
        candidate_name: inviteForm.candidate_name.trim(),
        candidate_email: inviteForm.candidate_email.trim(),
        job_id: inviteForm.job_id || undefined,
      });
      setCreatedLink(data.link);
      toast.success(data.email_sent ? 'Invite sent by email' : 'Invite created — copy the link below');
      setInviteForm({ candidate_name: '', candidate_email: '', job_id: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create invite');
    } finally {
      setInviting(false);
    }
  };

  const toggleSelect = (id, status) => {
    if (status !== 'completed') { toast.info('Only completed assessments can be compared'); return; }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size >= 4) { toast.info('Compare up to 4 at a time'); return next; }
      else next.add(id);
      return next;
    });
  };

  const runCompare = async () => {
    if (selected.size < 2) { toast.info('Select at least 2 completed assessments'); return; }
    try {
      const { data } = await behavioralAPI.compare(Array.from(selected));
      setCompareData(data.candidates); setCompareOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not build comparison');
    }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete the psychometric assessment for ${name}?`)) return;
    try { await behavioralAPI.remove(id); toast.success('Deleted'); load(); }
    catch { toast.error('Could not delete'); }
  };

  const resend = async (id) => {
    try { const { data } = await behavioralAPI.resend(id); toast[data.status === 'sent' ? 'success' : 'error'](data.status === 'sent' ? 'Reminder sent' : 'Could not send reminder'); }
    catch { toast.error('Could not send reminder'); }
  };

  return (
    <div data-testid="behavioral-assessments-page">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.6rem', fontWeight: 700, color: '#002A38', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Brain size={24} color={TERRA} /> Psychometric Assessments
          </h1>
          <p style={{ fontSize: '0.85rem', color: '#78716C', marginTop: 4 }}>DISC-style psychometric profiling — measure how candidates naturally prefer to work.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {selected.size >= 2 && (
            <Button data-testid="behavioral-compare-button" onClick={runCompare} variant="outline" style={{ borderColor: TERRA, color: TERRA }}>
              <Users size={16} style={{ marginRight: 6 }} /> Compare ({selected.size})
            </Button>
          )}
          <Button data-testid="behavioral-invite-open-button" onClick={() => { setInviteOpen(true); setCreatedLink(''); }} style={{ background: '#002A38', color: '#fff' }}>
            <Send size={16} style={{ marginRight: 6 }} /> Invite candidate
          </Button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#78716C' }}>Loading…</div>
      ) : items.length === 0 ? (
        <div data-testid="behavioral-empty" style={{ background: '#fff', border: '1px dashed #E7E5E4', borderRadius: 16, padding: '3rem', textAlign: 'center' }}>
          <Brain size={40} color="#D6CFC6" style={{ margin: '0 auto 1rem' }} />
          <h3 style={{ fontFamily: 'Cabinet Grotesk', fontWeight: 700, color: '#002A38', fontSize: '1.1rem' }}>No psychometric assessments yet</h3>
          <p style={{ color: '#78716C', fontSize: '0.9rem', margin: '0.5rem 0 1.25rem' }}>Invite a candidate to complete a short psychometric profile.</p>
          <Button data-testid="behavioral-invite-empty-button" onClick={() => setInviteOpen(true)} style={{ background: TERRA, color: '#fff' }}>
            <Send size={16} style={{ marginRight: 6 }} /> Invite your first candidate
          </Button>
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #E7E5E4', borderRadius: 16, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: 720 }}>
              <thead>
                <tr style={{ background: '#EDF4F7', textAlign: 'left', color: '#78716C', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <th style={{ padding: '0.75rem 1rem', width: 36 }}></th>
                  <th style={{ padding: '0.75rem 1rem' }}>Candidate</th>
                  <th style={{ padding: '0.75rem 1rem' }}>Role</th>
                  <th style={{ padding: '0.75rem 1rem' }}>Primary style</th>
                  <th style={{ padding: '0.75rem 1rem' }}>Status</th>
                  <th style={{ padding: '0.75rem 1rem' }}>Completed</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => {
                  const sb = statusBadge(s.status);
                  return (
                    <tr key={s.id} data-testid={`behavioral-row-${s.id}`} style={{ borderTop: '1px solid #F1EDE8' }}>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <input type="checkbox" data-testid={`behavioral-select-${s.id}`} checked={selected.has(s.id)} onChange={() => toggleSelect(s.id, s.status)} disabled={s.status !== 'completed'} style={{ accentColor: TERRA, width: 16, height: 16, cursor: s.status === 'completed' ? 'pointer' : 'not-allowed' }} />
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <div style={{ fontWeight: 600, color: '#002A38' }}>{s.candidate_name}</div>
                        <div style={{ color: '#A8A29E', fontSize: '0.75rem' }}>{s.candidate_email}</div>
                      </td>
                      <td style={{ padding: '0.75rem 1rem', color: '#57534E' }}>{s.job_title || '—'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}><StyleTag code={s.style_code} label={s.style_label} /></td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <span style={{ background: sb.bg, color: sb.color, borderRadius: 999, padding: '3px 10px', fontSize: '0.72rem', fontWeight: 700 }}>{sb.label}</span>
                        {s.status === 'in_progress' && <span style={{ fontSize: '0.7rem', color: '#A8A29E', marginLeft: 6 }}>{s.answered_count}/{s.total_questions}</span>}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', color: '#78716C', fontSize: '0.78rem' }}>{s.completed_at ? new Date(s.completed_at).toLocaleDateString() : '—'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          {s.status === 'completed' ? (
                            <button data-testid={`behavioral-view-${s.id}`} title="View report" onClick={() => navigate(`/behavioral-assessments/${s.id}`)} style={iconBtn}><FileText size={16} /></button>
                          ) : (
                            <button data-testid={`behavioral-resend-${s.id}`} title="Send reminder" onClick={() => resend(s.id)} style={iconBtn}><RefreshCw size={16} /></button>
                          )}
                          <button data-testid={`behavioral-delete-${s.id}`} title="Delete" onClick={() => del(s.id, s.candidate_name)} style={{ ...iconBtn, color: '#DC2626' }}><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Role Fit & Team Chemistry (job-scoped) */}
      <div ref={fitPanelRef} style={{ marginTop: '1.75rem', scrollMarginTop: '1rem' }} data-testid="behavioral-rolefit-section">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: '0.9rem' }}>
          <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.15rem', fontWeight: 700, color: '#002A38', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Target size={18} color={TERRA} /> Role Fit &amp; Team Chemistry
          </h2>
          <select data-testid="behavioral-rolefit-job-select" value={fitJobId} onChange={(e) => selectFitJob(e.target.value)}
            style={{ border: '1px solid #E7E5E4', borderRadius: 8, padding: '0.5rem 0.65rem', fontSize: '0.85rem', background: '#fff', minWidth: 240 }}>
            <option value="">Choose a role…</option>
            {jobs.map((j) => <option key={j._id} value={j._id}>{j.title}</option>)}
          </select>
        </div>
        {fitJobId ? (
          <BehavioralFitPanel key={fitJobId} jobId={fitJobId} />
        ) : (
          <div style={{ background: '#fff', border: '1px dashed #E7E5E4', borderRadius: 14, padding: '2rem', textAlign: 'center', color: '#78716C', fontSize: '0.85rem' }}>
            Pick a role to set its ideal DISC profile and see how candidates fit and blend as a team.
          </div>
        )}
      </div>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="behavioral-invite-dialog" className="sm:max-w-[440px]">
          <DialogHeader><DialogTitle>Invite a candidate</DialogTitle></DialogHeader>
          {createdLink ? (
            <div data-testid="behavioral-invite-success">
              <p style={{ fontSize: '0.85rem', color: '#57534E', marginBottom: 12 }}>Invite created. The candidate has been emailed a link. You can also copy it here:</p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: '#EDF4F7', borderRadius: 10, padding: '0.6rem 0.75rem' }}>
                <Link2 size={16} color="#78716C" />
                <span style={{ flex: 1, fontSize: '0.78rem', color: '#44403C', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{createdLink}</span>
                <button data-testid="behavioral-copy-created-link" onClick={() => copyLink(createdLink)} style={iconBtn}><Copy size={16} /></button>
              </div>
              <DialogFooter style={{ marginTop: 16 }}>
                <Button variant="outline" onClick={() => setCreatedLink('')}>Invite another</Button>
                <Button onClick={() => setInviteOpen(false)} style={{ background: '#002A38', color: '#fff' }}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <Label htmlFor="bh-name">Candidate name</Label>
                <Input id="bh-name" data-testid="behavioral-invite-name" value={inviteForm.candidate_name} onChange={(e) => setInviteForm({ ...inviteForm, candidate_name: e.target.value })} placeholder="Jordan Rivera" />
              </div>
              <div>
                <Label htmlFor="bh-email">Candidate email</Label>
                <Input id="bh-email" type="email" data-testid="behavioral-invite-email" value={inviteForm.candidate_email} onChange={(e) => setInviteForm({ ...inviteForm, candidate_email: e.target.value })} placeholder="jordan@example.com" />
              </div>
              <div>
                <Label htmlFor="bh-job">Link to a role (optional)</Label>
                <select id="bh-job" data-testid="behavioral-invite-job" value={inviteForm.job_id} onChange={(e) => setInviteForm({ ...inviteForm, job_id: e.target.value })}
                  style={{ width: '100%', border: '1px solid #E7E5E4', borderRadius: 8, padding: '0.5rem 0.6rem', fontSize: '0.875rem', background: '#fff', marginTop: 4 }}>
                  <option value="">No role — standalone</option>
                  {jobs.map((j) => <option key={j._id} value={j._id}>{j.title}</option>)}
                </select>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
                <Button data-testid="behavioral-invite-submit" onClick={submitInvite} disabled={inviting} style={{ background: TERRA, color: '#fff' }}>
                  {inviting ? 'Sending…' : 'Send invite'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Compare dialog */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent data-testid="behavioral-compare-dialog" className="sm:max-w-[900px]" style={{ maxHeight: '88vh', overflowY: 'auto' }}>
          <DialogHeader><DialogTitle>Compare profiles</DialogTitle></DialogHeader>
          {compareData && (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${compareData.length}, minmax(0,1fr))`, gap: 16 }}>
              {compareData.map((c) => (
                <div key={c.id} data-testid={`behavioral-compare-col-${c.id}`} style={{ border: '1px solid #E7E5E4', borderRadius: 14, padding: '1rem' }}>
                  <div style={{ fontWeight: 700, color: '#002A38', fontSize: '0.95rem' }}>{c.candidate_name}</div>
                  <StyleTag code={c.style_code} label={c.style_label} />
                  <div style={{ marginTop: 14 }}>
                    <BarGraph title="Public self" data={c.graph1} testid={`cmp-g1-${c.id}`} />
                    <DivergingGraph title="Adapted style" data={c.graph3} testid={`cmp-g3-${c.id}`} />
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter><Button onClick={() => setCompareOpen(false)} style={{ background: '#002A38', color: '#fff' }}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const iconBtn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, border: '1px solid #E7E5E4', background: '#fff', color: '#57534E', cursor: 'pointer' };
